import { LegacyQuotaDomainUsage, QuotaCategoryUsage, QuotaStatus } from '../types';

const CATEGORY_ORDER = ['chat_agent', 'public_reports', 'legacy_unclassified'] as const;
const CATEGORY_LABELS: Record<string, string> = {
  chat_agent: 'סוכן צ׳אט',
  public_reports: 'ניתוח דוחות ציבוריים',
  legacy_unclassified: 'שימוש היסטורי לא מסווג',
};

/** Local usage is useful even when W3 does not expose a quota limit. */
export const shouldShowQuota = (q: QuotaStatus | null | undefined): boolean => !!q;

/** Server-computed monthly total, with response-shape fallbacks during rolling deploys. */
export const usageTotal = (q: QuotaStatus): number => {
  if (typeof q.tracked_used === 'number' && Number.isFinite(q.tracked_used)) {
    return Math.max(0, q.tracked_used);
  }
  const rows: Array<{ tokens: number }> = q.by_category?.length
    ? q.by_category
    : q.by_domain || [];
  return rows.reduce((total, row) => total + Math.max(0, row.tokens || 0), 0);
};

export const hasQuotaLimit = (q: QuotaStatus): boolean => (
  q.available === true && typeof q.limit === 'number' && q.limit > 0
);

/** Used/limit as an integer 0–100, guarded and clamped. */
export const usagePercent = (used: number | null, limit: number | null): number => {
  if (!limit || limit <= 0 || used == null || used < 0) return 0;
  return Math.min(100, Math.round((used / limit) * 100));
};

export interface BreakdownRow {
  category: string;
  label: string;
  tokens: number;
}

/** Known products first; future server categories remain visible automatically. */
export const breakdownRows = (
  byCategory: QuotaCategoryUsage[] | undefined,
  legacyByDomain?: LegacyQuotaDomainUsage[],
): BreakdownRow[] => {
  const totals = new Map<string, number>();
  (byCategory || []).forEach((row) => {
    totals.set(row.category, (totals.get(row.category) || 0) + (row.tokens || 0));
  });

  // Old backend instances can still answer during a rolling deploy. Their
  // expertise domains are intentionally not guessed into product categories.
  if (!byCategory?.length) {
    const legacyTotal = (legacyByDomain || []).reduce(
      (total, row) => total + Math.max(0, row.tokens || 0),
      0,
    );
    if (legacyTotal > 0) totals.set('legacy_unclassified', legacyTotal);
  }

  const knownRows = CATEGORY_ORDER
    .filter((category) => category !== 'legacy_unclassified' || (totals.get(category) || 0) > 0)
    .map((category) => ({
      category,
      label: CATEGORY_LABELS[category],
      tokens: totals.get(category) || 0,
    }));
  const futureRows = Array.from(totals.keys())
    .filter((category) => !CATEGORY_ORDER.includes(category as typeof CATEGORY_ORDER[number]))
    .sort()
    .map((category) => ({ category, label: CATEGORY_LABELS[category] || category, tokens: totals.get(category) || 0 }));
  return [...knownRows, ...futureRows];
};

/** Compact token count for the pill (—, 500, 12K, 2.5M). */
export const formatTokens = (n: number | null | undefined): string => {
  if (n == null) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${Math.round(n / 1_000)}K`;
  return String(n);
};

export const formatExactTokens = (n: number | null | undefined): string => (
  n == null ? '—' : Math.max(0, n).toLocaleString('he-IL')
);

export const formatUsagePeriod = (yearMonth: string): string => {
  const match = /^(\d{4})-(\d{2})$/.exec(yearMonth || '');
  if (!match) return 'החודש';
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, 1));
  return new Intl.DateTimeFormat('he-IL', { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(date);
};
